import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RefreshCcw, Cloud, CheckCircle2, XCircle, Download, Upload, Link2, Trash2, Zap, ClipboardList, Mail, Send } from "lucide-react";
import { toast } from "sonner";

function StatusPill({ status }) {
  if (!status) return null;
  if (!status.configured) {
    return <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#fee2e2] text-[#991b1b]"><XCircle size={12}/> Nicht konfiguriert</span>;
  }
  if (status.reachable) {
    return <span data-testid="woo-status-ok" className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#d1fae5] text-[#065f46]"><CheckCircle2 size={12}/> Verbunden</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#fef3c7] text-[#854d0e]"><XCircle size={12}/> Nicht erreichbar</span>;
}

export default function Integrations() {
  const [wooStatus, setWooStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [lastSync, setLastSync] = useState(null);

  // SKU mapping
  const [unmatched, setUnmatched] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [items, setItems] = useState([]);
  const [picker, setPicker] = useState({}); // woo_sku -> item_id

  // Cron
  const [cron, setCron] = useState(null);
  const [cronLogs, setCronLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  // Email
  const [email, setEmail] = useState({ enabled: false, recipients: [], hour_utc: 7, configured: false });
  const [emailInput, setEmailInput] = useState("");

  // Overdue reminders
  const [reminder, setReminder] = useState({ enabled: false, days_after_due: 7, bcc: [] });
  const [reminderBcc, setReminderBcc] = useState("");
  const [reorderCfg, setReorderCfg] = useState({ enabled: false, recipients: [], hour_utc: 8, critical_only: true });
  const [reorderInput, setReorderInput] = useState("");

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/integrations/woocommerce/status");
      setWooStatus(data);
    } catch (err) { setWooStatus({ configured: false }); }
  };
  const loadMappings = async () => {
    const [u, m] = await Promise.all([
      api.get("/integrations/woocommerce/unmatched-skus"),
      api.get("/integrations/woocommerce/sku-mappings"),
    ]);
    setUnmatched(u.data);
    setMappings(m.data);
  };
  const loadCron = async () => {
    const { data } = await api.get("/integrations/woocommerce/cron");
    setCron(data);
  };
  const loadCronLogs = async () => {
    const { data } = await api.get("/integrations/woocommerce/cron/logs");
    setCronLogs(data);
  };
  const loadEmail = async () => {
    const { data } = await api.get("/integrations/email/settings");
    setEmail(data);
    setEmailInput((data.recipients || []).join(", "));
  };
  const loadReminder = async () => {
    const { data } = await api.get("/integrations/email/overdue-settings");
    setReminder(data);
    setReminderBcc((data.bcc || []).join(", "));
  };
  const loadReorderCfg = async () => {
    const { data } = await api.get("/integrations/email/reorder-settings");
    setReorderCfg(data);
    setReorderInput((data.recipients || []).join(", "));
  };

  useEffect(() => {
    loadStatus();
    api.get("/items").then((r) => setItems(r.data));
    loadMappings();
    loadCron();
    loadEmail();
    loadReminder();
    loadReorderCfg();
  }, []);

  const importOrders = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/integrations/woocommerce/orders/import?hours=168");
      const orders = data.orders || [];
      const withUnmatched = orders.filter((o) => o.unmatched && o.unmatched.length).length;
      const dnCount = orders.filter((o) => o.delivery_note).length;
      const invCount = orders.filter((o) => o.invoice).length;
      setLastImport({ ...data, delivery_notes_created: dnCount, invoices_created: invCount });
      toast.success(
        `${data.fetched} Bestellungen · ${dnCount} Lieferscheine · ${invCount} Rechnungen` +
        (withUnmatched ? ` (${withUnmatched} mit unbekannten SKUs)` : ""),
        { duration: 6000 },
      );
      loadMappings();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setImporting(false); }
  };

  const syncStock = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/woocommerce/stock/sync");
      setLastSync(data);
      toast.success(`${data.updated.length} Artikel synchronisiert, ${data.errors.length} Fehler`);
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setSyncing(false); }
  };

  const saveMapping = async (wooSku) => {
    const itemId = picker[wooSku];
    if (!itemId) { toast.error("Bitte Artikel wählen"); return; }
    try {
      await api.post("/integrations/woocommerce/sku-mappings", { woo_sku: wooSku, item_id: itemId });
      toast.success(`Zugeordnet: ${wooSku}`);
      setPicker((p) => { const n = { ...p }; delete n[wooSku]; return n; });
      loadMappings();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const deleteMapping = async (wooSku) => {
    if (!window.confirm(`Zuordnung "${wooSku}" wirklich löschen?`)) return;
    try {
      await api.delete(`/integrations/woocommerce/sku-mappings/${encodeURIComponent(wooSku)}`);
      toast.success("Gelöscht");
      loadMappings();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const toggleCron = async () => {
    try {
      const { data } = await api.post("/integrations/woocommerce/cron/toggle");
      toast.success(data.enabled ? "Auto-Sync AKTIV (alle 15 Min)" : "Auto-Sync deaktiviert");
      loadCron();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const runCronNow = async () => {
    try {
      const { data } = await api.post("/integrations/woocommerce/cron/run-now");
      toast.success(`Sync ausgeführt: ${data.orders?.fetched ?? 0} Bestellungen · ${data.stock?.updated ?? 0} Bestand`);
      loadCron(); loadCronLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const openLogs = async () => {
    if (!showLogs) await loadCronLogs();
    setShowLogs(!showLogs);
  };

  const saveEmail = async () => {
    const recipients = emailInput.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await api.post("/integrations/email/settings", {
        enabled: email.enabled,
        recipients,
        hour_utc: email.hour_utc,
      });
      toast.success("Benachrichtigungs-Einstellungen gespeichert");
      loadEmail();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const testEmailNow = async () => {
    try {
      const { data } = await api.post("/integrations/email/test-send");
      if (data.error) toast.error(`Fehler: ${data.error}`);
      else if (data.skipped) toast.warning(`Übersprungen: ${data.skipped}`);
      else if (data.reason === "no_low_stock") toast.info("Kein Artikel unter Min-Bestand – keine Mail versendet");
      else toast.success(`Mail gesendet an ${data.sent} Empfänger (${data.rows} Artikel)`);
      loadEmail();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const saveReminder = async () => {
    const bcc = reminderBcc.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await api.post("/integrations/email/overdue-settings", {
        enabled: reminder.enabled,
        days_after_due: reminder.days_after_due,
        bcc,
      });
      toast.success("Zahlungserinnerung gespeichert");
      loadReminder();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const runReminderNow = async () => {
    try {
      const { data } = await api.post("/integrations/email/overdue-run");
      if (data.skipped) toast.warning(`Übersprungen: ${data.skipped}`);
      else toast.success(`${data.sent} Erinnerung(en) gesendet · ${data.skipped_no_email} ohne Email · ${data.errors?.length || 0} Fehler`);
      loadReminder();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const saveReorderCfg = async () => {
    const recipients = reorderInput.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await api.post("/integrations/email/reorder-settings", {
        enabled: reorderCfg.enabled,
        recipients,
        hour_utc: reorderCfg.hour_utc,
        critical_only: reorderCfg.critical_only,
      });
      toast.success("Auto-Bestellvorschlag gespeichert");
      loadReorderCfg();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const runReorderNow = async () => {
    try {
      const { data } = await api.post("/integrations/email/reorder-test-send");
      if (data.error) toast.error(`Fehler: ${data.error}`);
      else if (data.skipped) toast.warning(`Übersprungen: ${data.skipped}`);
      else if (data.reason === "no_critical") toast.info("Keine Zutat unter kritischer Reichweite – keine Mail versendet");
      else toast.success(`Mail an ${data.sent} Empfänger · ${data.critical} kritische Zutaten · ${data.groups} Lieferanten`);
      loadReorderCfg();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  return (
    <div data-testid="page-integrations">
      <PageHeader
        eyebrow="Schnittstellen"
        title="Integrationen"
        description="Verbindung zu externen Systemen. Automatisch oder auf Knopfdruck."
      />

      <Card className="p-6 border border-[var(--border)] rounded-2xl mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[var(--pop-pink)]/10 flex items-center justify-center text-[var(--pop-pink)]">
              <Cloud size={20} />
            </div>
            <div>
              <div className="font-display text-xl font-bold">WooCommerce</div>
              <div className="text-xs text-[var(--text-muted)]">B2C-Shop – Bestellungen abrufen, Bestand pushen</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={wooStatus} />
            <button onClick={loadStatus} data-testid="btn-woo-refresh" className="text-xs text-[var(--text-muted)] hover:text-[var(--text)]" title="Status neu laden">
              <RefreshCcw size={14} />
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="border border-[var(--border)] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2 font-semibold"><Download size={16}/> Bestellungen importieren</div>
            <p className="text-xs text-[var(--text-muted)] mb-3">
              Zieht Bestellungen der letzten 7 Tage aus dem Shop.
            </p>
            <Button onClick={importOrders} disabled={importing || !wooStatus?.reachable}
                    data-testid="btn-woo-import"
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              {importing ? "Läuft…" : "Bestellungen ziehen"}
            </Button>
            {lastImport && (
              <div className="mt-3 text-xs text-[var(--text-muted)] space-y-1">
                <div>Abgerufen: <b>{lastImport.fetched}</b></div>
                {(lastImport.orders || []).slice(0, 3).map((o, i) => (
                  <div key={i} className="font-mono">{o.number} · {o.lines} Zeilen{o.unmatched?.length ? ` · unbekannt: ${o.unmatched.join(",")}` : ""}</div>
                ))}
              </div>
            )}
          </div>

          <div className="border border-[var(--border)] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2 font-semibold"><Upload size={16}/> Bestand pushen</div>
            <p className="text-xs text-[var(--text-muted)] mb-3">
              Setzt <code>stock_quantity</code> aller Fertigwaren im Shop.
            </p>
            <Button onClick={syncStock} disabled={syncing || !wooStatus?.reachable}
                    data-testid="btn-woo-sync"
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              {syncing ? "Läuft…" : "Bestand synchronisieren"}
            </Button>
            {lastSync && (
              <div className="mt-3 text-xs text-[var(--text-muted)] space-y-1">
                <div>Aktualisiert: <b>{lastSync.updated.length}</b> · Fehler: <b>{lastSync.errors.length}</b></div>
              </div>
            )}
          </div>
        </div>

        {/* Auto-Sync */}
        <div className="border border-[var(--border)] rounded-xl p-4 mb-4 bg-[var(--pop-cream)]/40">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Zap size={18} className={cron?.enabled ? "text-[var(--success)]" : "text-[var(--text-muted)]"} />
              <div>
                <div className="font-semibold">Auto-Sync alle 15 Minuten</div>
                <div className="text-xs text-[var(--text-muted)]">
                  Zieht Bestellungen (letzte 30 Min) und pusht Bestand automatisch.
                  {cron?.last_run && <> Zuletzt: <span className="font-mono">{cron.last_run.slice(0, 19).replace("T", " ")}</span></>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={openLogs} data-testid="btn-toggle-cron-logs"
                      className="text-xs text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-1">
                <ClipboardList size={14} /> Log {showLogs ? "verbergen" : "anzeigen"}
              </button>
              <button onClick={runCronNow} data-testid="btn-run-cron-now"
                      className="text-xs text-[var(--text-muted)] hover:text-[var(--text)]">
                Jetzt ausführen
              </button>
              <Button onClick={toggleCron} data-testid="btn-toggle-cron" variant={cron?.enabled ? "default" : "outline"}
                      className={`rounded-full font-bold ${cron?.enabled ? "bg-[var(--success)] text-white hover:opacity-90" : ""}`}>
                {cron?.enabled ? "AKTIV – deaktivieren" : "Auto-Sync aktivieren"}
              </Button>
            </div>
          </div>
          {showLogs && (
            <div className="mt-4 border-t border-[var(--border)] pt-3">
              <div className="text-xs font-semibold uppercase text-[var(--text-muted)] mb-2">Letzte {cronLogs.length} Läufe</div>
              <div className="space-y-1 max-h-64 overflow-y-auto font-mono text-xs">
                {cronLogs.map((l) => (
                  <div key={l.id} data-testid={`cron-log-${l.id}`} className="flex flex-wrap items-center gap-3 py-1 border-b border-[var(--border)]/50">
                    <span className="text-[var(--text-muted)]">{(l.at || l.created_at || "").slice(0, 19).replace("T", " ")}</span>
                    <span>Bestellungen: <b className="text-[var(--text)]">{l.orders?.fetched ?? "—"}</b>{l.orders?.error ? ` (${l.orders.error})` : ""}</span>
                    <span>Bestand: <b className="text-[var(--text)]">{l.stock?.updated ?? "—"}</b>
                      {l.stock?.errors ? <span className="text-[var(--danger)]"> ({l.stock.errors} err)</span> : null}
                    </span>
                  </div>
                ))}
                {cronLogs.length === 0 && <div className="text-[var(--text-muted)]">Noch keine Läufe.</div>}
              </div>
            </div>
          )}
        </div>

        {/* SKU Mapping */}
        <div className="border border-[var(--border)] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3 font-semibold"><Link2 size={16}/> SKU-Zuordnung</div>
          <p className="text-xs text-[var(--text-muted)] mb-3">
            Unbekannte Woo-SKUs auf POPCRN-Artikel abbilden. Beim nächsten Import werden diese Zeilen dann korrekt zugeordnet.
          </p>

          {unmatched.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold uppercase text-[var(--text-muted)] mb-2">
                Offen ({unmatched.length})
              </div>
              <div className="space-y-2">
                {unmatched.map((u) => (
                  <div key={u.woo_sku} data-testid={`unmatched-row-${u.woo_sku}`}
                       className="flex flex-wrap items-center gap-2 p-2 border border-[var(--border)] rounded-lg">
                    <span className="font-mono text-sm flex-shrink-0">{u.woo_sku}</span>
                    <span className="text-xs text-[var(--text-muted)] flex-shrink-0">({u.occurrences}×)</span>
                    <span className="text-[var(--text-muted)] flex-shrink-0">→</span>
                    <div className="flex-1 min-w-[200px]">
                      <Select value={picker[u.woo_sku] || ""}
                              onValueChange={(v) => setPicker((p) => ({ ...p, [u.woo_sku]: v }))}>
                        <SelectTrigger data-testid={`select-item-${u.woo_sku}`} className="h-9 rounded-lg">
                          <SelectValue placeholder="Artikel wählen…" />
                        </SelectTrigger>
                        <SelectContent>
                          {items.map((it) => (
                            <SelectItem key={it.id} value={it.id}>{it.sku} · {it.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button size="sm" onClick={() => saveMapping(u.woo_sku)}
                            data-testid={`btn-save-mapping-${u.woo_sku}`}
                            className="rounded-full bg-[var(--pop-pink)] text-white hover:opacity-90 font-bold">
                      Zuordnen
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mappings.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-[var(--text-muted)] mb-2">Gespeichert ({mappings.length})</div>
              <div className="space-y-1">
                {mappings.map((m) => (
                  <div key={m.woo_sku} data-testid={`mapping-row-${m.woo_sku}`}
                       className="flex items-center gap-2 text-sm">
                    <span className="font-mono">{m.woo_sku}</span>
                    <span className="text-[var(--text-muted)]">→</span>
                    <span className="font-mono text-xs">{m.item_sku}</span>
                    <span>· {m.item_name}</span>
                    <button onClick={() => deleteMapping(m.woo_sku)}
                            data-testid={`btn-del-mapping-${m.woo_sku}`}
                            className="text-[var(--danger)] hover:opacity-80 ml-auto">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {unmatched.length === 0 && mappings.length === 0 && (
            <div className="text-sm text-[var(--text-muted)]">Keine unbekannten SKUs. Alle Bestellungen konnten zugeordnet werden.</div>
          )}
        </div>
      </Card>

      {/* Email notifications card */}
      <Card className="p-6 border border-[var(--border)] rounded-2xl mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#fef3c7] flex items-center justify-center text-[#854d0e]">
              <Mail size={20} />
            </div>
            <div>
              <div className="font-display text-xl font-bold">E-Mail-Benachrichtigung</div>
              <div className="text-xs text-[var(--text-muted)]">Tägliche Zusammenfassung – Artikel unter Min-Bestand</div>
            </div>
          </div>
          {email.configured
            ? <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#d1fae5] text-[#065f46]"><CheckCircle2 size={12}/> Bereit</span>
            : <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#fee2e2] text-[#991b1b]"><XCircle size={12}/> EMERGENT_EMAIL_KEY fehlt</span>
          }
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-3">
          <div>
            <label className="text-xs font-semibold uppercase text-[var(--text-muted)]">Empfänger (Komma-getrennt)</label>
            <input value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                   data-testid="input-email-recipients"
                   placeholder="einkauf@popcrn.de, chef@popcrn.de"
                   className="w-full mt-1 h-10 px-3 rounded-lg border border-[var(--border)] font-mono text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[var(--text-muted)]">Uhrzeit (UTC)</label>
            <div className="flex items-center gap-3 mt-1">
              <input type="number" min="0" max="23" value={email.hour_utc}
                     onChange={(e) => setEmail({ ...email, hour_utc: Number(e.target.value) })}
                     data-testid="input-email-hour"
                     className="w-24 h-10 px-3 rounded-lg border border-[var(--border)] font-mono text-center" />
              <span className="text-xs text-[var(--text-muted)]">→ ca. {(email.hour_utc + 1) % 24}:00 Berlin (Winter) / {(email.hour_utc + 2) % 24}:00 (Sommer)</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!email.enabled}
                   onChange={(e) => setEmail({ ...email, enabled: e.target.checked })}
                   data-testid="checkbox-email-enabled"
                   className="accent-[var(--pop-pink)] w-4 h-4" />
            <span>Tägliche Zusammenfassung aktivieren</span>
          </label>
          <div className="flex items-center gap-2">
            <button onClick={testEmailNow} data-testid="btn-test-email"
                    className="text-xs text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-1">
              <Send size={14} /> Testmail jetzt
            </button>
            <Button onClick={saveEmail} data-testid="btn-save-email"
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              Speichern
            </Button>
          </div>
        </div>

        {email.last_sent && (
          <div className="mt-3 text-xs text-[var(--text-muted)]">
            Zuletzt gesendet: <span className="font-mono">{email.last_sent.slice(0, 19).replace("T", " ")}</span>
          </div>
        )}
      </Card>

      {/* Overdue payment reminder card */}
      <Card className="p-6 border border-[var(--border)] rounded-2xl mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#fee2e2] flex items-center justify-center text-[#991b1b]">
              <Mail size={20} />
            </div>
            <div>
              <div className="font-display text-xl font-bold">Zahlungserinnerung an Kunden</div>
              <div className="text-xs text-[var(--text-muted)]">Freundliche Erinnerungs-Mail an Kunden mit überfälligen Rechnungen</div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-3">
          <div>
            <label className="text-xs font-semibold uppercase text-[var(--text-muted)]">Erinnerung nach (Tagen)</label>
            <input type="number" min="1" max="90" value={reminder.days_after_due}
                   onChange={(e) => setReminder({ ...reminder, days_after_due: Number(e.target.value) })}
                   data-testid="input-reminder-days"
                   className="w-full mt-1 h-10 px-3 rounded-lg border border-[var(--border)] font-mono text-center" />
            <div className="text-xs text-[var(--text-muted)] mt-1">Standard: 7 Tage nach Fälligkeit</div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[var(--text-muted)]">Interner BCC (optional)</label>
            <input value={reminderBcc} onChange={(e) => setReminderBcc(e.target.value)}
                   data-testid="input-reminder-bcc"
                   placeholder="buchhaltung@popcrn.de"
                   className="w-full mt-1 h-10 px-3 rounded-lg border border-[var(--border)] font-mono text-sm" />
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!reminder.enabled}
                   onChange={(e) => setReminder({ ...reminder, enabled: e.target.checked })}
                   data-testid="checkbox-reminder-enabled"
                   className="accent-[var(--pop-pink)] w-4 h-4" />
            <span>Zahlungserinnerungen automatisch senden (täglich 09:00 UTC)</span>
          </label>
          <div className="flex items-center gap-2">
            <button onClick={runReminderNow} data-testid="btn-reminder-run"
                    className="text-xs text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-1">
              <Send size={14} /> Jetzt ausführen
            </button>
            <Button onClick={saveReminder} data-testid="btn-save-reminder"
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              Speichern
            </Button>
          </div>
        </div>

        {reminder.last_result && (
          <div className="mt-3 text-xs text-[var(--text-muted)]">
            Letzter Lauf: <span className="font-mono">{(reminder.last_run || "").slice(0, 19).replace("T", " ")}</span>
            · Gesendet: <b>{reminder.last_result.sent || 0}</b>
            {reminder.last_result.skipped_no_email ? ` · Ohne Email: ${reminder.last_result.skipped_no_email}` : ""}
            {reminder.last_result.errors?.length ? ` · Fehler: ${reminder.last_result.errors.length}` : ""}
          </div>
        )}
      </Card>

      {/* Auto-Reorder Email Card */}
      <Card className="p-6 border border-[var(--border)] rounded-2xl mb-6" data-testid="card-reorder-email">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#fef3c7] flex items-center justify-center text-[#854d0e]">
              <Mail size={20} />
            </div>
            <div>
              <div className="font-display text-xl font-bold">Automatischer Bestellvorschlag</div>
              <div className="text-xs text-[var(--text-muted)]">Tägliche E-Mail mit PDF-Anhang, sobald eine Zutat unter die Lieferzeit reicht</div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-3">
          <div>
            <label className="text-xs font-semibold uppercase text-[var(--text-muted)]">Empfänger (Komma-getrennt)</label>
            <input value={reorderInput} onChange={(e) => setReorderInput(e.target.value)}
                   data-testid="input-reorder-recipients"
                   placeholder="einkauf@popcrn.de, dennis@popcrn.de"
                   className="w-full mt-1 h-10 px-3 rounded-lg border border-[var(--border)] font-mono text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[var(--text-muted)]">Uhrzeit (UTC)</label>
            <input type="number" min="0" max="23" value={reorderCfg.hour_utc}
                   onChange={(e) => setReorderCfg({ ...reorderCfg, hour_utc: Number(e.target.value) })}
                   data-testid="input-reorder-hour"
                   className="w-full mt-1 h-10 px-3 rounded-lg border border-[var(--border)] font-mono text-center" />
            <div className="text-xs text-[var(--text-muted)] mt-1">Standard 08:00 UTC ≈ 09:00 Berlin</div>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!reorderCfg.critical_only}
                   onChange={(e) => setReorderCfg({ ...reorderCfg, critical_only: e.target.checked })}
                   data-testid="checkbox-reorder-critical"
                   className="accent-[var(--pop-pink)] w-4 h-4" />
            <span>Nur kritische Zutaten (Reichweite &lt; Lieferzeit)</span>
          </label>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!reorderCfg.enabled}
                   onChange={(e) => setReorderCfg({ ...reorderCfg, enabled: e.target.checked })}
                   data-testid="checkbox-reorder-enabled"
                   className="accent-[var(--pop-pink)] w-4 h-4" />
            <span>Auto-Bestellvorschlag täglich versenden</span>
          </label>
          <div className="flex items-center gap-2">
            <button onClick={runReorderNow} data-testid="btn-reorder-run"
                    className="text-xs text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-1">
              <Send size={14} /> Jetzt ausführen
            </button>
            <Button onClick={saveReorderCfg} data-testid="btn-save-reorder"
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              Speichern
            </Button>
          </div>
        </div>

        {reorderCfg.last_result && (
          <div className="mt-3 text-xs text-[var(--text-muted)]" data-testid="reorder-last-result">
            Letzter Lauf: <span className="font-mono">{(reorderCfg.last_run || "").slice(0, 19).replace("T", " ")}</span>
            {reorderCfg.last_result.critical !== undefined && ` · Kritisch: ${reorderCfg.last_result.critical}`}
            {reorderCfg.last_result.error && ` · Fehler: ${reorderCfg.last_result.error}`}
          </div>
        )}
      </Card>
    </div>
  );
}
